/* eslint-disable @typescript-eslint/no-magic-numbers */
import type { JSX } from 'react';
import type { EmbedOptions } from '~/playerEmbedOptions/types';
import { isNonEmptyArray } from '~/utilities/type-guards';

export const DEFAULT_PLAY_BUTTON_WIDTH = 125;
export const DEFAULT_PLAY_BUTTON_HEIGHT = 80;

const gradientCSS = `
  #play-button-rect {
    fill:url(#gradient);
  }
`;

type Props = {
  embedOptions: EmbedOptions;
  isVerticalVideo: boolean;
  isPreview?: boolean;
  width?: number;
  height?: number;
  buttonScale?: number;
};

export const PlayButton = ({
  embedOptions,
  isVerticalVideo,
  isPreview = false,
  width = DEFAULT_PLAY_BUTTON_WIDTH,
  height = DEFAULT_PLAY_BUTTON_HEIGHT,
  buttonScale = 1,
}: Props): JSX.Element | string => {
  const { playerColor, roundedPlayer, playerColorGradient } = embedOptions;
  const borderRadius = (((roundedPlayer ?? 0) / DEFAULT_PLAY_BUTTON_WIDTH) * 100).toFixed(2);
  const radius = `${borderRadius}%`;
  const hasGradient =
    playerColorGradient?.on === true && isNonEmptyArray(playerColorGradient.colors);

  const color1 = hasGradient ? playerColorGradient.colors[0][0] : playerColor;
  const color2 = hasGradient ? playerColorGradient.colors[1][0] : playerColor;
  const offset1 = hasGradient ? playerColorGradient.colors[0][1] * 100 : 0;
  const offset2 = hasGradient ? playerColorGradient.colors[1][1] * 100 : 100;
  const playBtnToThumbnailRatio = isVerticalVideo ? 0.3 : 0.13;
  const gradientScale = 1 / playBtnToThumbnailRatio;
  const translate = (1 - playBtnToThumbnailRatio) / 2;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      transform={isPreview ? 'scale(0.25)' : 'scale(1)'}
    >
      {hasGradient && (
        <>
          <style type="text/css">{gradientCSS}</style>
          <defs>
            <linearGradient
              id="gradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
              gradientTransform={`scale(${gradientScale}) translate(-${translate})`}
            >
              <stop
                stopColor={color1}
                offset={`${offset1}%`}
              />
              <stop
                stopColor={color2}
                offset={`${offset2}%`}
              />
            </linearGradient>
          </defs>
        </>
      )}
      <rect
        id="play-button-rect"
        x="0"
        y="0"
        width={width}
        height={height}
        opacity=".9"
        fill={`#${playerColor}`}
        rx={radius}
        ry={radius}
      />
      <path
        fill="#fff"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M56 24.36c-1.326-.92-3.138.03-3.138 1.643V54.37c0 1.613 1.812 2.563 3.138 1.644L76.487 41.83a2 2 0 0 0 0-3.288L56 24.36Z"
        transform={`scale(${buttonScale})`}
      />
    </svg>
  );
};
